/**
 * Dev-only re-homing of absolute abax CDN URLs onto the webpack dev server's
 * same-origin proxy.
 *
 * Every deployed environment consumes the CDN same-origin - the CDN is a path
 * on the app's own host - and the CDN buckets intentionally serve no
 * Access-Control-Allow-Origin header (the deploy pipeline documents that
 * posture). Remote-entry scripts are loaded with `crossorigin` (required for
 * SRI), so the one cross-origin consumer class - a dev document on
 * `localhost`/`127.0.0.1` configured with an absolute CDN URL - hard-fails
 * the CORS check. (That hostname set deliberately mirrors validate-url.ts;
 * other dev document origins are out of scope for both gates.)
 * The dev server proxies the CDN path prefixes instead; re-homing a
 * configured absolute CDN URL onto that proxy gives dev the same same-origin
 * topology the deployed environments have. Manifest bytes pass through the
 * proxy untouched, so signature verification is unaffected, and the derived
 * publicPath / webpack `auto` publicPath both resolve against the re-homed
 * origin, keeping every subsequent chunk load same-origin too.
 *
 * In a production bundle the NODE_ENV constant folds to `false` and this
 * function collapses to the identity - deployed URL handling never changes.
 */

// Must stay in lockstep with the dev-server proxy entries in
// webpack.config.js (each pathPrefix needs a proxy context serving it).
const CDN_PROXY_PREFIXES: ReadonlyArray<{ origin: string; pathPrefix: string }> = [
  { origin: 'https://staging.track2find.com', pathPrefix: '/abaxcdnstaging/' },
  { origin: 'https://abax.cloud', pathPrefix: '/abaxcdn/' },
];

const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

export function rehomeCdnUrlForDevProxy(url: string): string {
  if (!IS_DEV_BUILD || !url) return url;
  if (!LOCALHOST_HOSTNAMES.has(window.location.hostname)) return url;
  try {
    const parsed = new URL(url);
    const matches = CDN_PROXY_PREFIXES.some(
      ({ origin, pathPrefix }) =>
        parsed.origin === origin && parsed.pathname.startsWith(pathPrefix),
    );
    if (!matches) return url;
    return `${window.location.origin}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}
